import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, CheckCircle, Star, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function ConfidenceOutfitGuide() {
  const navigate = useNavigate();

  const outfitElements = [
    { element: "Color", tip: "Choose colors that make your face look bright and alive, not washed out" },
    { element: "Fit", tip: "Proper fit is everything. Not too tight, not too loose—just right for your shape" },
    { element: "Comfort", tip: "If you're tugging, adjusting, or uncomfortable, you won't feel confident" },
    { element: "Statement Piece", tip: "One piece that makes you feel powerful—a jacket, shoes, jewelry" },
    { element: "Complete Look", tip: "Hair done, accessories that fit the vibe, shoes that complete it" }
  ];

  const occasions = [
    { occasion: "Important Meeting", elements: ["Structured blazer", "Quality basics", "Statement earrings", "Comfortable heels or flats"] },
    { occasion: "First Date", elements: ["Flattering dress or jeans + nice top", "Signature scent", "Effortless hair", "Comfortable but cute shoes"] },
    { occasion: "Girls' Night Out", elements: ["Your favorite 'fun' piece", "Bold lip or eye", "Dancing shoes", "Clutch or crossbody"] },
    { occasion: "Everyday Confidence", elements: ["Well-fitting jeans", "Elevated basic top", "Quality accessories", "Clean, comfy sneakers or flats"] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
            Week 7 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Confidence Outfit Planning
          </h1>
          <p className="text-xl text-secondary-600">Dress for the Confidence You Want</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <ShoppingBag className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Task</h3>
              <p className="text-emerald-100 text-lg">
                Plan 5 'confidence outfits' for different occasions. Lay them out, photograph them, 
                and keep the photos in your phone for decision fatigue days.
              </p>
            </div>
          </div>
        </section>

        {/* Elements */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Elements of a Confidence Outfit</h2>
          <div className="space-y-3">
            {outfitElements.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl">
                <Star className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <span className="font-bold text-emerald-700">{item.element}:</span>
                  <span className="text-secondary-600 ml-2">{item.tip}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Occasion Outfits */}
        <section className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Outfit Ideas by Occasion</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {occasions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-5">
                <h3 className="font-bold text-emerald-700 mb-3">{item.occasion}</h3>
                <ul className="space-y-2">
                  {item.elements.map((el, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {el}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ConfidenceOutfitGuide;
